// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

/**
 * Viewport screenshot capture for feedback reports.
 *
 * html2canvas-pro is imported lazily so the library only reaches the network
 * when a user actually opts into attaching a screenshot. The pro fork is
 * required because Tailwind v4 emits oklch()/oklab() colors that the original
 * html2canvas cannot parse.
 */

/** Attribute placed on every feedback-owned overlay element so the capture
 *  can exclude the dialog itself from its own screenshot. */
export const FEEDBACK_UI_ATTR = 'data-feedback-ui';

export async function captureViewportScreenshot(): Promise<Blob> {
  const { default: html2canvas } = await import('html2canvas-pro');

  const canvas = await html2canvas(document.body, {
    logging: false,
    useCORS: true,
    // Viewport only — a full-page capture would defeat the size budget.
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    width: window.innerWidth,
    height: window.innerHeight,
    scale: Math.min(window.devicePixelRatio || 1, 2),
    backgroundColor: readBackground(),
    ignoreElements: (el) =>
      el instanceof HTMLElement && el.closest(`[${FEEDBACK_UI_ATTR}]`) !== null,
  });

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.8),
  );
  if (!blob) {
    throw new Error('Screenshot capture failed.');
  }
  return blob;
}

function readBackground(): string {
  const bg = getComputedStyle(document.body).backgroundColor;
  return bg && bg !== 'rgba(0, 0, 0, 0)' ? bg : '#ffffff';
}
